import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import {
  PARTNER_DEAL_INTERNAL_STAGES,
  PARTNER_DEAL_STAGES,
  PartnerDealResponse,
  PartnerDealStage,
  partnerDealStageLabel,
  partnerDealStatusLabel,
  toPartnerVisibleStage,
} from '@interfaces/partners/responses/partner-deal.response';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Roles } from '@app/core/guard/Roles';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { ModalValorDealComponent } from './components/modal-valor-deal/modal-valor-deal.component';
import {
  ModalMoveBackStageComponent,
  MoveBackStageConfirmed,
} from './components/modal-move-back-stage/modal-move-back-stage.component';
import { DealActionsMenuComponent } from './components/deal-actions-menu/deal-actions-menu.component';
import { KanbanBoardComponent } from '@shared/components/kanban-board/kanban-board.component';
import {
  KanbanBoardColumn,
  KanbanColumnTotal,
} from '@shared/components/kanban-board/kanban-board.types';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PartnerDealsRealtimeService } from '@features/dashboard/commercial/services/partner-deals-realtime.service';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { compareBoardOrder, dropPosition, renumberColumn } from './deal-board-order';
import { DealBankJourneyStore } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey.store';
import {
  DealJourneySwitchComponent,
  DealJourneyView,
} from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-journey-switch/deal-journey-switch.component';
import { MyDealsBankJourneyComponent } from './components/my-deals-bank-journey/my-deals-bank-journey.component';

/**
 * Colunas do pipeline, na ordem do funil.
 *
 * A ordem é a do enum do backend e **não** é alfabética: um deal caminha de Registro até
 * Cotação. Os rótulos com acento existem porque os valores do enum não os têm — o enum é
 * identificador, isto aqui é texto de tela.
 */
const STAGES: ReadonlyArray<{ value: PartnerDealStage; label: string }> = [
  { value: 'Registro', label: 'Registro' },
  { value: 'Consentimento', label: 'Consentimento' },
  { value: 'Documentacao', label: 'Documentação' },
  // Gates e Análise entraram no enum em 23/09/2026 (card 2952) e ainda não têm dado por trás.
  // As colunas existem mesmo assim: sem elas, um deal movido para essas etapas sumiria do board
  // — e "sumiu" é o pior sintoma possível, porque não parece erro.
  // ⚠️ Gates é etapa INTERNA (card 3088): a coluna só aparece para o time interno (`stages`); para
  // o parceiro o deal em Gates está em Documentação.
  { value: 'Gates', label: 'Gates' },
  { value: 'Analise', label: 'Análise' },
  { value: 'Comite', label: 'Comitê' },
  { value: 'Cotacao', label: 'Cotação' },
  { value: 'Contratacao', label: 'Contrato enviado' },
  { value: 'Operacao', label: 'Contrato assinado' },
];

/**
 * Cor da pílula por etapa.
 *
 * Cada situação tem a sua cor, como na referência de design: "aguardando alguém" é
 * âmbar, "em análise nossa" é azul, comitê é roxo, e o fim do funil é verde. Isso é
 * independente do <b>tom</b> da barra lateral, que fala de urgência (prazo da reserva,
 * documento com falha) — as duas informações não são a mesma e não podem dividir cor.
 */
const STAGE_COLOR: Readonly<Record<PartnerDealStage, string>> = {
  Registro: 'ambar',
  Consentimento: 'ambar',
  Documentacao: 'azul',
  // Gates e Análise são etapas nossas, como Documentação — mesmo azul de "em análise nossa".
  Gates: 'azul',
  Analise: 'azul',
  Comite: 'roxo',
  Cotacao: 'ambar',
  Contratacao: 'indigo',
  Operacao: 'verde',
};

/**
 * Id da coluna sintética dos rejeitados (card 3612). Rejeitado não é etapa: o deal continua com o `stage`
 * de onde parou, e é o `rejectedAt` que o põe nesta coluna.
 */
const REJECTED_COLUMN_ID = 'Rejected';

/** Coluna do board: uma etapa ou a coluna dos rejeitados. */
type DealColumnId = PartnerDealStage | typeof REJECTED_COLUMN_ID;

/** Coluna do board compartilhado (card 3213): o id é a etapa (ou `Rejected`). */
interface ColunaDeDeals extends KanbanBoardColumn {
  id: DealColumnId;
}

/** Um dia em milissegundos, para a contagem do prazo da reserva. */
const MillisecondsInADay = 86_400_000;

/**
 * Tela "Meus Deals" — pipeline arrastável do módulo Comercial (card 2903).
 *
 * Inspirada no kanban do pipeline de crédito, mas **sem herdar o acoplamento dele**:
 * *"lá acabou ficando muito fechado para o pipeline de negociação"* (reunião de
 * 08/09/2026). Aqui o modelo é o `PartnerDeal`, as colunas vêm do enum de etapas e o
 * drag-and-drop é o nativo do HTML5 — nada de estender a entidade `Pipeline`.
 *
 * O mesmo pipeline atende dois perfis: o parceiro vê os deals dele; o usuário interno da
 * Legatus vê os dele e os dos parceiros. Quem faz esse recorte é o backend — a tela só
 * desenha o que recebe.
 */
@Component({
  selector: 'app-my-deals',
  standalone: true,
  // `RouterLink` explícito: o `SharedModule` IMPORTA o `RouterModule` para os templates
  // dele, mas não o reexporta. Sem isto, `[routerLink]` no link do card vira atributo
  // inerte — a âncora nasce sem `href` e o clique não navega, sem erro nenhum de build.
  // Medido em 10/09/2026 clicando no link.
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    RouterLink,
    ModalValorDealComponent,
    ModalMoveBackStageComponent,
    KanbanBoardComponent,
    DealActionsMenuComponent,
    DealJourneySwitchComponent,
    MyDealsBankJourneyComponent,
  ],
  templateUrl: './my-deals.component.html',
  styleUrls: ['./my-deals.component.scss'],
  // Tempo real (card 3496): relê o board quando um deal muda em outra sessão.
  // `DealBankJourneyStore`: os bancos de todos os deals da visão Jornada nos bancos (card 3765).
  providers: [RealtimeListRefreshService, PartnerDealsRealtimeService, DealBankJourneyStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyDealsComponent extends VaultBaseComponent implements OnInit {
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  /**
   * Colunas do board para quem está vendo: o parceiro não tem as etapas internas (Gates, card
   * 3088; Comitê e Cotação). Nasce na visão do parceiro e só ganha as internas quando o papel interno
   * resolve — o erro aceitável é o tenant ver a coluna um instante depois, nunca o parceiro vê-la por um
   * instante.
   *
   * Operação voltou a ser coluna do parceiro no card 3707: de 3612 até ali o deal em Operação saía do quadro
   * e ia para a Jornada nos bancos ao lado; com a jornada dentro do deal (aba Bancos), sem a coluna o deal
   * sumiria da tela.
   */
  readonly stages = computed(() =>
    this.visaoInterna() ? STAGES : STAGES.filter(s => !PARTNER_DEAL_INTERNAL_STAGES.has(s.value))
  );

  /**
   * As etapas como colunas do `app-kanban-board` (card 3213), com a coluna "Rejeitados" antes de
   * Contratação (card 3612). Rótulo do parceiro fora da visão interna ("NDA e Bacen", "Contrato
   * enviado"). O board é o mesmo do Pipeline de Crédito; esta tela só entrega colunas, listas e o card.
   */
  readonly colunas = computed<ColunaDeDeals[]>(() => {
    const internal = this.visaoInterna();
    const columns: ColunaDeDeals[] = [];
    for (const stage of this.stages()) {
      if (stage.value === 'Contratacao') {
        columns.push({ id: REJECTED_COLUMN_ID, name: 'Rejeitados' });
      }
      columns.push({ id: stage.value, name: partnerDealStageLabel(stage.value, internal) });
    }
    return columns;
  });

  // Adaptadores do board. Arrow functions de propósito: o board chama sem o `this` da tela.
  readonly ativosDe = (coluna: ColunaDeDeals): PartnerDealResponse[] => this.dealsOf(coluna.id);
  readonly contagemDe = (coluna: ColunaDeDeals): number => this.dealsOf(coluna.id).length;
  /** Uma linha por moeda; coluna sem deal mostra "BRL: (0) 0", como antes da troca de board. */
  readonly totaisDe = (coluna: ColunaDeDeals): KanbanColumnTotal[] => {
    const totais = this.totaisPorMoeda(coluna.id).map(t => ({
      currency: t.moeda,
      count: t.quantidade,
      value: t.total,
    }));
    return totais.length ? totais : [{ currency: 'BRL', count: 0, value: 0 }];
  };
  /**
   * Sem deal nenhum: a coluna vira só a área de soltar, sem texto. Os textos de coluna
   * vazia ("Nenhum deal em análise." e afins) saíram no card 3558: poluíam o board e não diziam nada
   * que a coluna vazia já não dissesse. Registrar continua no botão Registrar deal.
   */
  readonly colunaVazia = (coluna: ColunaDeDeals): boolean => this.dealsOf(coluna.id).length === 0;
  /**
   * Rejeitado não se arrasta: mudar a etapa de um deal parado não o reabre, só esconde de onde ele
   * parou. Desde o card 3612 o board aplica esta trava também na lista principal, onde fica a coluna
   * Rejeitados.
   */
  readonly arrasteTravado = (deal: PartnerDealResponse): boolean => !!deal.rejectedAt;

  /** Há um card sendo arrastado agora: acende o "Soltar aqui!" da coluna sob o cursor. */
  readonly arrastando = signal(false);
  /** Rodapé de totais por moeda recolhido (o chevron do pé do board). */
  readonly resumoRecolhido = signal(false);

  /** Se quem está vendo é do time interno (TENANT/ADMIN). Decide se as etapas internas aparecem. */
  readonly visaoInterna = signal(false);
  readonly deals = signal<PartnerDealResponse[]>([]);
  readonly loading = signal(false);

  /**
   * Quem pode arrastar card entre colunas — ou seja, quem move a etapa do deal.
   *
   * Só time interno (TENANT/ADMIN). A etapa é a leitura que a Legatus faz do funil: se o
   * parceiro pudesse arrastar, ele declararia sozinho que o próprio deal está "em comitê"
   * ou "em contratação". Ele acompanha; quem move é quem conduz a negociação.
   *
   * Isto é a metade visível da regra — o backend recusa o PATCH de etapa para PARTNER,
   * senão bastaria chamar o endpoint direto.
   */
  readonly podeArrastar = signal(false);

  /**
   * Se o rótulo de origem do deal aparece no card.
   *
   * Só para o time interno. O board do parceiro mostra **apenas os deals dele**, então ali o
   * rótulo era constante — dizia "com você" em todos os cards, em qualquer etapa, e não
   * informava nada (decisão do Breno em 11/09/2026). Ele só distingue alguma coisa na visão
   * interna, onde deal trazido por parceiro convive com deal originado pela própria Legatus.
   */
  readonly mostraOrigem = signal(false);

  /**
   * Parceiros selecionados no filtro. Lista **vazia = todos**; `LEGATUS` = os originados pela
   * casa. Múltipla escolha a pedido do Breno em 14/09/2026 — comparar dois parceiros lado a
   * lado no funil era impossível com seleção única.
   *
   * Vazio significando "todos" é o que dispensa uma opção "Todos" na lista: com múltipla
   * escolha ela seria um item que se contradiz com os outros (marcar "Todos" e "Parceiro B"
   * ao mesmo tempo não quer dizer nada). Quem comunica isso é o `placeholder` do seletor.
   *
   * Só existe na visão interna, e só lá faz sentido: o board do parceiro já é um recorte de
   * um parceiro só. Filtra em memória porque a listagem vem inteira numa requisição — não há
   * paginação para respeitar, e ir ao servidor a cada troca de filtro seria trabalho a mais
   * por uma resposta que já está na mão.
   */
  readonly filtrosDeParceiro = signal<string[]>([]);

  /** Valor sentinela do filtro para "deals da própria Legatus" (os de `partnerId` nulo). */
  readonly FILTRO_LEGATUS = 'LEGATUS';

  /**
   * Parceiros presentes no board, para montar o filtro.
   *
   * Sai dos próprios deals carregados, e não de um endpoint de parceiros: a lista útil é a de
   * quem tem deal aqui, não a de todos os parceiros cadastrados — filtro com opção que não
   * casa com nada é ruído.
   */
  readonly parceirosDoBoard = computed(() => {
    const porId = new Map<string, string>();
    for (const deal of this.deals()) {
      if (deal.partnerId && deal.partnerName) porId.set(deal.partnerId, deal.partnerName);
    }

    return [...porId.entries()]
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  });

  /**
   * Visão da tela (card 3765): o quadro de deals ou a **Jornada nos bancos** de todos os deals, pelo mesmo seletor
   * do cabeçalho do deal. Vai para a URL (`?view=banks`), para o link e o F5 voltarem na mesma visão.
   */
  readonly bankView = signal(false);

  /**
   * A visão geral dos bancos (todos os deals) é só do **parceiro** (card 3848, Breno em 31/10/2026): o time interno
   * acompanha os bancos dentro de cada deal (seletor do cabeçalho do deal), não numa visão de todos. Só liga depois de
   * o papel resolver, para o interno não ver o seletor nem por um instante.
   */
  readonly roleResolved = signal(false);
  readonly banksAvailable = computed(() => this.roleResolved() && !this.visaoInterna());

  /** A visão dos bancos de fato na tela: `?view=banks` de um interno cai no quadro de deals. */
  readonly activeBankView = computed(() => this.bankView() && this.banksAvailable());
  protected readonly bankJourney = inject(DealBankJourneyStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  /** Deal sendo arrastado. Nulo fora do arrasto. */
  private dragging: PartnerDealResponse | null = null;

  /** Tempo real (card 3496): quando reler o board. */
  private readonly partnerDealsRealtime = inject(PartnerDealsRealtimeService);

  /** Coluna sob o cursor, para o destaque visual. */
  readonly dragOverStage = signal<DealColumnId | null>(null);

  constructor(
    translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly formErrorHandler: FormErrorHandlerService,
    private readonly partnersService: PartnersHttpService,
    private readonly dateFormatter: DateFormatterService,
    private readonly permissionsService: PermissionsService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    // Nasce com o texto do parceiro e é reescrito quando o papel resolve (abaixo): a consulta
    // de papel é assíncrona, e deixar o breadcrumb vazio até lá faria a faixa pular.
    this.breadcrumbs = [{ label: 'Comercial' }, { label: this.titulo }];
    this.load();
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.bankView.set(params.get('view') === 'banks'));

    // Tempo real (card 3496): outra sessão mudou um deal; relê sem skeleton (espera o arrasto). Os bancos relêem
    // junto (card 3765): ligar uma negociação, mover o banco e comentar também publicam a mudança do deal.
    this.partnerDealsRealtime.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.load(true);
      if (this.banksAvailable()) this.bankJourney.reload();
    });

    // Papel, e não permissão: mover etapa não é uma ação que se conceda a um parceiro
    // configurando grupo — é do time que conduz a negociação.
    this.permissionsService
      .hasRole([Roles.TENANT, Roles.ADMIN])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pode => {
        this.podeArrastar.set(pode);
        // Mesma pergunta ("é do time interno?"), respostas independentes de propósito: quem um
        // dia puder arrastar sem ser interno não deve, por isso, ganhar o rótulo de origem.
        this.mostraOrigem.set(pode);
        this.visaoInterna.set(pode);
        this.partnerDealsRealtime.setInternalView(pode);
        // Os bancos carregam junto com o quadro, para o seletor já mostrar quantos são (ou o cadeado). Só para o
        // parceiro (card 3848): o interno não tem a visão geral dos bancos.
        if (!pode && !this.roleResolved()) this.bankJourney.setAllDeals();
        this.roleResolved.set(true);
        this.breadcrumbs = [{ label: 'Comercial' }, { label: this.titulo }];
        this.cdr.markForCheck();
      });
  }

  /**
   * Deals de uma coluna: os que contam no número e no total do cabeçalho.
   *
   * Rejeitado não é etapa: o `stage` continua dizendo onde o deal parou, e é isso que permite
   * reabri-lo de lá. Desde o card 3612 ele mora na coluna própria "Rejeitados" (antes era uma seção
   * no pé da coluna onde parou), com o mesmo filtro de parceiro e busca dos demais.
   */
  dealsOf(columnId: DealColumnId): PartnerDealResponse[] {
    if (columnId === REJECTED_COLUMN_ID) {
      return this.deals().filter(d => !!d.rejectedAt && this.passaNoFiltro(d));
    }
    return this.deals().filter(
      d => this.etapaExibida(d) === columnId && !d.rejectedAt && this.passaNoFiltro(d)
    );
  }

  /**
   * Etapa em que o card aparece para quem está vendo. O backend já devolve a etapa projetada para o
   * parceiro (Gates → Documentação, card 3088); a projeção repetida aqui é defesa em profundidade,
   * para um deal numa etapa interna nunca cair numa coluna que o parceiro não tem.
   */
  private etapaExibida(deal: PartnerDealResponse): PartnerDealStage {
    return this.visaoInterna() ? deal.stage : toPartnerVisibleStage(deal.stage);
  }

  /**
   * Se o deal sobrevive ao filtro de parceiro.
   *
   * Mora aqui, e não numa lista pré-filtrada, porque os totais por coluna somam exatamente o
   * que a coluna mostra (`totalOf` chama `dealsOf`) — filtrar num lugar só evita o clássico
   * "o board mostra 3 cards e o total é de 16".
   */
  private passaNoFiltro(deal: PartnerDealResponse): boolean {
    if (!this.passaNaBusca(deal)) return false;

    const filtros = this.filtrosDeParceiro();
    if (!filtros.length) return true;

    // Deal da Legatus não tem `partnerId`; a sentinela ocupa o lugar dele para que a
    // pergunta seja uma só ("este deal está entre os selecionados?"), em vez de um caso
    // especial antes da comparação.
    return filtros.includes(deal.partnerId ?? this.FILTRO_LEGATUS);
  }

  /**
   * Texto da busca da faixa de ferramentas (card 3066), no molde do "Pesquisar negociações..."
   * do Pipeline de Crédito. Procura na razão social, no CNPJ (com ou sem máscara), no número
   * do deal e no nome do parceiro. Mora no mesmo `passaNoFiltro` do filtro de parceiro, então
   * contagem, colunas e totais continuam somando exatamente o que aparece.
   */
  readonly busca = signal('');

  private passaNaBusca(deal: PartnerDealResponse): boolean {
    const termo = this.normalizar(this.busca());
    if (!termo) return true;

    const digitos = termo.replace(/\D/g, '');
    const cnpj = (deal.cnpj ?? '').replace(/\D/g, '');

    return (
      this.normalizar(deal.corporateName).includes(termo) ||
      this.normalizar(deal.partnerName).includes(termo) ||
      String(deal.sequentialId ?? '').includes(termo) ||
      (digitos.length >= 3 && cnpj.includes(digitos))
    );
  }

  /** Minúsculas e sem acento: "sao paulo" acha "SÃO PAULO". */
  private normalizar(texto: string | null | undefined): string {
    return (texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  onBuscaChange(valor: string | null): void {
    this.busca.set(valor ?? '');
  }

  /** Quantos deals estão visíveis com o filtro atual — o contador do topo. */
  readonly totalVisivel = computed(() => this.deals().filter(d => this.passaNoFiltro(d)).length);

  /** Se há busca ou filtro de parceiro em uso — quando há, o contador mostra "de N". */
  readonly filtrando = computed(() => !!this.busca().trim() || this.filtrosDeParceiro().length > 0);

  /**
   * Colunas recolhidas (card 3066), como o chevron das colunas do Pipeline de Crédito: a coluna
   * vira uma faixa de 50px com o título na vertical. Serve para tirar do caminho as etapas que a
   * pessoa não está olhando — o board tem 9 colunas e não cabe numa tela.
   */
  readonly colunasRecolhidas = signal<ReadonlySet<DealColumnId>>(new Set());

  alternarColuna(columnId: DealColumnId): void {
    this.colunasRecolhidas.update(atual => {
      const proximo = new Set(atual);
      if (!proximo.delete(columnId)) proximo.add(columnId);
      return proximo;
    });
  }

  /**
   * Opções do seletor de parceiro: cada parceiro do board e "Da Legatus".
   *
   * Sem opção "Todos": no seletor múltiplo ela seria um item que contradiz os demais — ver
   * `filtrosDeParceiro`. Nenhum marcado já é "todos", e o placeholder diz isso.
   *
   * "Da Legatus" só entra quando existe deal sem parceiro — oferecer um filtro que devolve
   * zero card é pior do que não oferecer.
   */
  readonly opcoesDeFiltro = computed<Option[]>(() => {
    const opcoes: Option[] = this.parceirosDoBoard().map(parceiro => ({
      label: parceiro.nome,
      value: parceiro.id,
    }));

    if (this.deals().some(d => !d.partnerId)) {
      opcoes.push({ label: 'Da Legatus', value: this.FILTRO_LEGATUS });
    }

    return opcoes;
  });

  /**
   * Ponte entre o `ngModel` do `app-select` múltiplo e o sinal do filtro.
   *
   * O wrapper é um `ControlValueAccessor` e só aceita `ngModel`/`FormControl` — ligar por
   * input paralelo é anti-padrão declarado do projeto. Em modo múltiplo ele entrega um array,
   * e `null` quando o usuário desmarca tudo pelo "selecionar todos" — daí o `?? []`.
   */
  onFiltroDeParceiroChange(valores: string[] | null): void {
    this.filtrosDeParceiro.set(valores ?? []);
  }

  /**
   * Troca entre o quadro de deals e a Jornada nos bancos (card 3765), pela URL. A busca é da faixa e serve às duas
   * visões, mas o que se procura muda (deal x banco ou empresa): ela é limpa na troca, para a outra visão não abrir
   * filtrada por um texto que não faz sentido nela.
   */
  onJourneyChange(view: DealJourneyView): void {
    this.busca.set('');
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: view === 'banks' ? 'banks' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Título da tela: "meus" só é verdade para o parceiro. */
  get titulo(): string {
    return this.mostraOrigem() ? 'Deals' : 'Meus deals';
  }

  /**
   * Situação do deal, em uma frase — a pílula do card.
   *
   * Deriva do que existe (etapa + andamento dos documentos); o backend não tem um campo
   * de sub-status. Documento em andamento ou com falha vence a etapa porque é o que
   * exige ação agora.
   */
  statusLabel(deal: PartnerDealResponse): string {
    // Rejeitado vence tudo: a etapa só diz onde ele parou, e um "Aguardando documentos" num
    // deal parado seria uma instrução que ninguém vai cumprir. A regra é a mesma do Início.
    return partnerDealStatusLabel(deal, this.etapaExibida(deal));
  }

  /**
   * Cor da pílula de situação.
   *
   * Separada do tom da barra de propósito: a pílula diz <b>o que</b> está acontecendo
   * (cor por etapa), a barra diz <b>quão urgente</b> é. Documento em falha é a exceção
   * em que as duas coincidem, porque aí a situação é a urgência.
   */
  statusColor(deal: PartnerDealResponse): string {
    if (deal.rejectedAt) return 'vermelho';
    if (deal.documentStatus === 'Failed') return 'vermelho';
    if (deal.documentStatus === 'Processing') return 'azul';

    return STAGE_COLOR[this.etapaExibida(deal)] ?? 'cinza';
  }

  /** Tom da barra lateral do card: `alerta`, `atencao` ou `neutro`. */
  statusTone(deal: PartnerDealResponse): 'alerta' | 'atencao' | 'neutro' {
    if (deal.rejectedAt) return 'neutro';
    if (deal.documentStatus === 'Failed') return 'alerta';
    if (this.reservationDaysLeft(deal) !== null && this.reservationDaysLeft(deal)! <= 15)
      return 'atencao';

    return 'neutro';
  }

  /**
   * Origem do deal no rodapé do card: **o nome do parceiro** que o trouxe.
   *
   * Nulo para deal da Legatus, e o template esconde a linha nesse caso — o selo `LEGATUS` no
   * topo do card já diz isso, e repetir "da Legatus" embaixo gastava a única linha livre do
   * rodapé para dizer duas vezes a mesma coisa (pedido do Breno em 14/09/2026).
   *
   * O histórico explica por que não é um texto fixo: até 11/09/2026 dizia "com você" / "com a
   * Legatus", errado dos dois lados; virou "via parceiro" / "da Legatus", certo mas genérico —
   * na visão interna, onde o board mistura vários parceiros, saber QUE veio de parceiro não
   * ajuda, saber DE QUEM ajuda.
   *
   * `partnerName` só vem preenchido para o usuário interno (o backend não o busca para o
   * parceiro, onde seria sempre o mesmo). O fallback existe para o caso de um deal com
   * `partnerId` cujo nome não resolveu: melhor o rótulo genérico antigo do que um vazio que
   * parece deal da Legatus sem o selo.
   */
  origemLabel(deal: PartnerDealResponse): string | null {
    if (!deal.partnerId) return null;

    return deal.partnerName?.trim() || 'via parceiro';
  }

  /**
   * Dias restantes da reserva de CNPJ, ou nulo quando não há prazo.
   *
   * Nulo é o caso normal depois do primeiro documento: a reserva vira definitiva e o
   * `reservationExpiresAt` é zerado no backend.
   */
  reservationDaysLeft(deal: PartnerDealResponse): number | null {
    const expira = this.dateFormatter.parseDateTruncateTime(deal.reservationExpiresAt);
    if (!expira) return null;

    const hoje = this.dateFormatter.parseDateTruncateTime(this.dateFormatter.getTodayDate())!;
    return Math.ceil((expira.getTime() - hoje.getTime()) / MillisecondsInADay);
  }

  /** Texto do prazo da reserva, já resolvido para o vencido. */
  reservationLabel(deal: PartnerDealResponse): string | null {
    // Prazo de reserva num deal rejeitado é urgência sem ação possível.
    if (deal.rejectedAt) return null;
    const dias = this.reservationDaysLeft(deal);
    if (dias === null) return null;
    if (dias < 0) return 'reserva vencida';
    if (dias === 0) return 'reserva expira hoje';

    return `reserva expira em ${dias}d`;
  }

  /**
   * Totais da coluna **por moeda** (card 3066), como o rodapé do Pipeline de Crédito
   * ("BRL: (6) 3.147.402,03").
   *
   * Corrige um defeito do total único que existia no cabeçalho: ele somava `amount` de deals em
   * moedas diferentes e formatava o resultado como BRL — um deal de US$ 18 MM entrava como
   * R$ 18 MM. Somar moedas diferentes não tem conversão certa na tela (cálculo financeiro é do
   * backend), então cada moeda tem a sua linha. BRL vem primeiro; as demais em ordem alfabética.
   */
  totaisPorMoeda(stage: DealColumnId): { moeda: string; quantidade: number; total: number }[] {
    const porMoeda = new Map<string, { quantidade: number; total: number }>();
    for (const deal of this.dealsOf(stage)) {
      const moeda = (deal.currencyCode || 'BRL').toUpperCase();
      const atual = porMoeda.get(moeda) ?? { quantidade: 0, total: 0 };
      porMoeda.set(moeda, { quantidade: atual.quantidade + 1, total: atual.total + deal.amount });
    }

    return [...porMoeda.entries()]
      .map(([moeda, v]) => ({ moeda, ...v }))
      .sort((a, b) =>
        a.moeda === 'BRL' ? -1 : b.moeda === 'BRL' ? 1 : a.moeda.localeCompare(b.moeda)
      );
  }

  onDragStart(deal: PartnerDealResponse): void {
    // Rejeitado não se arrasta: mudar a etapa de um deal parado não o reabre, só esconde de
    // onde ele parou. Reabrir é uma decisão, com histórico, e mora na tela do deal.
    if (!this.podeArrastar() || deal.rejectedAt) return;
    this.dragging = deal;
    this.arrastando.set(true);
    this.partnerDealsRealtime.setDragging(true);
  }

  /** Fim do arrasto, com ou sem drop: apaga o destaque. */
  onDragEnd(): void {
    this.arrastando.set(false);
    this.partnerDealsRealtime.setDragging(false);
    this.dragOverStage.set(null);
    this.cdr.markForCheck();
  }

  onDragOver(stage: DealColumnId): void {
    // Quem não move etapa não vê a coluna se acender. O board já desliga o drop para ele
    // (`dragDisabled`); a guarda aqui é a mesma regra repetida, barata. A coluna Rejeitados não
    // recebe card (rejeitar é decisão com motivo, na tela do deal), então também não acende.
    if (!this.podeArrastar() || stage === REJECTED_COLUMN_ID) return;
    if (this.dragOverStage() !== stage) {
      this.dragOverStage.set(stage);
      this.cdr.markForCheck();
    }
  }

  onDragLeave(stage: DealColumnId): void {
    if (this.dragOverStage() === stage) {
      this.dragOverStage.set(null);
      this.cdr.markForCheck();
    }
  }

  /**
   * Solta o card numa coluna: move (ou reordena) na hora e persiste.
   *
   * Atualização otimista — a coluna muda antes da resposta. Se o backend recusar, o board volta
   * exatamente como estava e o erro aparece em toast; deixar o card onde caiu com o banco dizendo
   * outra coisa seria pior que o erro.
   *
   * Desde o card 3307 (T10) a posição conta: soltar na mesma coluna reordena, e soltar em outra
   * põe o card onde ele caiu. Regra e conta do índice em `deal-board-order.ts`.
   *
   * @param columnId coluna em que o card caiu (a etapa; `Rejected` é ignorada).
   * @param dropped o item que o board diz ter sido solto, para quando o `dragstart` não passou por
   *   aqui (o que não deveria acontecer, mas não custa).
   * @param dropIndex índice do drop na lista exibida, como o `ngx-drag-drop` entrega.
   */
  onDrop(columnId: DealColumnId, dropped?: PartnerDealResponse | null, dropIndex?: number): void {
    if (!this.podeArrastar()) return;

    const deal = this.dragging ?? dropped ?? null;
    this.dragging = null;
    this.arrastando.set(false);
    this.partnerDealsRealtime.setDragging(false);
    this.dragOverStage.set(null);

    // Soltar em Rejeitados não muda nada (card 3612): rejeitar pede motivo e mora na tela do deal.
    if (!deal || deal.rejectedAt || columnId === REJECTED_COLUMN_ID) {
      this.cdr.markForCheck();
      return;
    }
    const stage: PartnerDealStage = columnId;

    const column = this.activeColumn(stage);
    const position = dropPosition(column, this.dealsOf(stage), deal.id, dropIndex);
    const unchanged =
      deal.stage === stage && column.findIndex(d => d.id === deal.id) + 1 === position;
    if (unchanged) {
      this.cdr.markForCheck();
      return;
    }

    const before = this.deals();
    this.placeLocally(deal, stage, position);

    // Card 3579: voltar de etapa avisa o parceiro por e-mail com o motivo, então o motivo é pedido antes de
    // gravar. O card já fica na coluna nova enquanto a modal está aberta; cancelar devolve.
    if (this.isBackwardForPartner(deal.stage, stage)) {
      // Segura a atualização em tempo real enquanto a modal está aberta: uma releitura no meio
      // devolveria o card à coluna antiga e o Cancelar restauraria um board mais velho que ela.
      this.partnerDealsRealtime.setDragging(true);
      this.pendingMoveBackSnapshot = before;
      this.modalMoveBack?.open(deal, deal.stage, stage, position);
      return;
    }

    this.persistStage(deal, stage, position, before);
  }

  @ViewChild('modalMoveBack') private modalMoveBack?: ModalMoveBackStageComponent;

  /** Board antes da volta de etapa que espera o motivo; é para onde o cancelar devolve. */
  private pendingMoveBackSnapshot: PartnerDealResponse[] | null = null;

  /** O motivo foi confirmado na modal: grava a volta de etapa com ele. */
  onMoveBackConfirmed(event: MoveBackStageConfirmed): void {
    const before = this.pendingMoveBackSnapshot ?? this.deals();
    this.pendingMoveBackSnapshot = null;
    this.persistStage(event.deal, event.stage, event.position, before, event.reason);
    this.partnerDealsRealtime.setDragging(false);
  }

  /** A modal foi fechada sem motivo: o card volta para onde estava e nada é gravado. */
  onMoveBackCancelled(): void {
    if (this.pendingMoveBackSnapshot) this.deals.set(this.pendingMoveBackSnapshot);
    this.pendingMoveBackSnapshot = null;
    this.cdr.markForCheck();
    // Libera o tempo real; se chegou atualização com a modal aberta, ela é aplicada agora.
    this.partnerDealsRealtime.setDragging(false);
  }

  /**
   * Volta de etapa como o parceiro a vê: Gates conta como Documentação, então Gates → Documentação não é
   * volta para ele (não gera aviso nem e-mail) e não pede motivo.
   */
  private isBackwardForPartner(from: PartnerDealStage, to: PartnerDealStage): boolean {
    const fromIndex = PARTNER_DEAL_STAGES.indexOf(toPartnerVisibleStage(from));
    const toIndex = PARTNER_DEAL_STAGES.indexOf(toPartnerVisibleStage(to));
    return toIndex < fromIndex;
  }

  /** Grava a etapa (e a posição) do arraste; se o backend recusar, o board volta ao `before`. */
  private persistStage(
    deal: PartnerDealResponse,
    stage: PartnerDealStage,
    position: number,
    before: PartnerDealResponse[],
    reason?: string
  ): void {
    this.partnersService
      .updateDealStage(deal.id, stage, position, reason)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => {
          this.deals.set(before);
          this.cdr.markForCheck();
          this.formErrorHandler.showToast(
            error,
            'Não conseguimos salvar a nova posição do deal. O card voltou para onde estava; arraste de novo para tentar.'
          );
        },
      });
  }

  /** A coluna inteira em andamento (sem o filtro de parceiro), na ordem do board. */
  private activeColumn(stage: PartnerDealStage): PartnerDealResponse[] {
    return this.deals()
      .filter(d => this.etapaExibida(d) === stage && !d.rejectedAt)
      .sort(compareBoardOrder);
  }

  /** Põe o card na etapa e posição pedidas na lista local, renumerando a coluna como o backend. */
  private placeLocally(deal: PartnerDealResponse, stage: PartnerDealStage, position: number): void {
    const others = this.activeColumn(stage).filter(d => d.id !== deal.id);
    const orders = renumberColumn(others, deal, position);

    this.deals.update(current =>
      current
        .map(d =>
          d.id === deal.id
            ? { ...d, stage, boardOrder: orders.get(d.id) }
            : orders.has(d.id)
              ? { ...d, boardOrder: orders.get(d.id) }
              : d
        )
        .sort(compareBoardOrder)
    );
    this.cdr.markForCheck();
  }

  @ViewChild('modalValor') private modalValor?: ModalValorDealComponent;

  /**
   * Abre a modal de correção do valor pretendido.
   *
   * Só é chamada a partir de um card cujo `canEditAmount` veio verdadeiro — e esse campo é
   * resolvido no servidor pela régua "usuário interno, ou master do parceiro dono". A tela
   * não reimplementa a régua; e o endpoint recusa de todo modo, então esconder a ação é
   * conveniência, não autorização.
   */
  editarValor(deal: PartnerDealResponse): void {
    this.modalValor?.abrir(deal);
  }

  /** Se o card do deal mostra as reticências de ações (card 3324). */
  readonly hasActions = (deal: PartnerDealResponse): boolean =>
    DealActionsMenuComponent.hasActions(deal);

  /** Tira do board o deal apagado pelo menu, sem recarregar a lista inteira. */
  removeDeal(dealId: string): void {
    this.deals.update(current => current.filter(deal => deal.id !== dealId));
    this.cdr.markForCheck();
  }

  /** Recarrega o board depois que a modal salvou o valor. */
  carregar(): void {
    this.load();
  }

  /** Leitura do board em voo; uma leitura nova cancela a anterior (card 3496). */
  private loadSubscription: Subscription | null = null;
  /** Geração da leitura atual: só ela mexe no `loading` (a cancelada não apaga o skeleton). */
  private loadGeneration = 0;

  /**
   * Carrega os deals visíveis para quem está logado. Uma leitura nova sempre cancela a que estiver
   * em voo, então a tela nunca mostra uma resposta mais velha que a última pedida.
   * @param silent releitura do tempo real (card 3496): sem o skeleton, o board troca no lugar. Se
   *   ela cancelar uma leitura normal ainda em andamento, herda o skeleton e o toast de erro dela.
   */
  private load(silent = false): void {
    const showsLoading = !silent || this.loading();
    const generation = ++this.loadGeneration;
    this.loadSubscription?.unsubscribe();

    if (showsLoading) {
      this.loading.set(true);
    }

    this.loadSubscription = this.partnersService
      .getMyDeals()
      .pipe(
        finalize(() => {
          if (generation !== this.loadGeneration) return;
          this.loading.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: deals => this.deals.set(deals ?? []),
        error: error => {
          // Releitura do tempo real que falhou: o board continua como estava, sem toast.
          if (showsLoading) {
            this.formErrorHandler.showToast(error, 'Não foi possível carregar os deals.');
          }
        },
      });
  }
}
